import {
  UserIcon,
  ShoppingBagIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  TagIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CubeIcon,
  CreditCardIcon,
  TicketIcon,
  TruckIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  BriefcaseIcon,
  BookOpenIcon,
  BellAlertIcon,
  ClockIcon,
  MegaphoneIcon,
  UserGroupIcon,
  WalletIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ServerIcon,
  LightBulbIcon,
  CalculatorIcon,
  DevicePhoneMobileIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  CircleStackIcon,
  CloudIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
  CurrencyDollarIcon,
  HeartIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

import type {
  CategoryMeta,
  IconOptionsMap,
  CardOrganization,
  CardSettingsMap,
  CardSettingsEntry,
  CustomCategoriesMap,
  CategoryFormsMap,
  CategoryDisplayInfo
} from '../types/adminDashboard';

export const CARD_KEY_ALIASES: Record<string, string> = {
  'Investor Relations': 'Investors',
  'Blockchain Dashboard': 'Blockchain Management',
  'Users': 'User Management',
  'Real Estate Hub': 'Real Estate',
  'Real Estate & RWA Hub': 'Real Estate',
  'Sports Hub': 'Sports',
  'CMS': 'Landing CMS',
  'Landing Pages': 'Landing CMS',
  'Landing Page CMS': 'Landing CMS',
  'Content Management': 'Landing CMS'
};

export const CARD_DESCRIPTIONS: Record<string, string> = {
  'User Management': 'Manage all system users and roles.',
  'User Submissions': 'Review and approve submitted user ideas.',
  'User Status': 'Manage user status and rankings.',
  'Projects': 'Project workspace and task management.',
  'Listings': 'Manage all system listings.',
  'Shipping': 'Manage system shipping options.',
  'Carriers': 'Manage shipping carriers.',
  'Services': 'Manage system services.',
  'Deals': 'Manage system deals and promotions.',
  'Types Management': 'Manage all type definitions, categories, and classifications.',
  'ACL Management': 'Manage system users and roles.',
  'Roles': 'Manage user roles and permissions.',
  'Systems Control': 'Monitor system health and services.',
  'Server Manager': 'Manage server infrastructure.',
  'Analytics': 'View system analytics and insights.',
  'System Analytics': 'Advanced system performance metrics.',
  'Login Management': 'Manage login security settings.',
  'Compliance Portal': 'Monitor SOC 2 compliance and evidence.',
  'Plesk Management': 'Manage Plesk hosting panel.',
  'Blockchain Management': 'Manage blockchain and crypto assets.',
  'Commodity Trading': 'Manage commodity trading.',
  'Investment Pools': 'Manage investment pools.',
  'Real Estate': 'Physical property portfolio, subordinate debt, and specialty commercial experience pools.',
  'Sports': 'Manage sport catalogs, equipment types, and athletic setups.',
  'Inactive Companies': 'View inactive company accounts.',
  'Products': 'Manage all system products.',
  'Total Companies': 'Manage all system companies.',
  'Inventory': 'Manage system inventory.',
  'Payments': 'Manage payment transactions.',
  'Coupons': 'Manage discount coupons.',
  'Suppliers': 'Manage supplier accounts.',
  'Strains': 'Manage strain definitions.',
  'Knowledge Base': 'Manage knowledge base articles.',
  'Learning Management': 'Manage courses and learning.',
  'LMS Settings': 'Configure LMS system settings.',
  'Memberships': 'Manage user memberships.',
  'Investors': 'Review investor relations.',
  'KYC Providers': 'Manage KYC verification providers.',
  'Mail': 'Manage mail provider settings.',
  'SMS': 'Manage SMS provider settings.',
  'SIP': 'Manage SIP voice providers.',
  'Bot Settings': 'Configure bot notification settings.',
  'Unfinished Orders': 'Configure unfinished order handling.',
  'Docker': 'Manage Docker containers.',
  'Document Management': 'Manage system documents.',
  'Cloud Storage': 'Configure and monitor external cloud storage integrations.',
  'E-Signature': 'Manage e-signature workflows.',
  'Template Manager': 'Manage document templates.',
  'Legal Management': 'Manage BC legal practice areas, content, and client matters.',
  'CRM Management': 'Customer relationship management.',
  'Workflow Designer': 'Design automation workflows.',
  'Backup Management': 'Manage system backups.',
  'AI Management': 'Configure AI provider settings.',
  'Daily Quotes': 'Sources, history, and emergency bulletins.',
  'Accounting Management': 'Manage accounting operations.',
  'Commodity Marketplace': 'Manage the commodity marketplace.',
  'HR Management': 'Manage human resources.',
  'Navbar Editor': 'Configure navigation bar layout.',
  'Sidebar Editor': 'Configure sidebar navigation.',
  'Website Manager': 'Manage hosted websites.',
  'Website Builder': 'Build websites with AI.',
  'Domain Management': 'Manage domain registrations.',
  'Landing CMS': 'Manage landing pages, content sections, and AI design wizard.',
  'Support Tickets': 'Manage support ticket queue.',
  'Video Conference': 'Manage video conferences.',
  'Janus Management': 'Manage Janus media server.',
  'Janus Admin': 'Monitor Janus server health.',
  'Janus Transcription': 'Manage call transcriptions.',
  'Log Viewer': 'View system log files.',
  'Chat Management': 'Manage chat channels.',
  'Financial Services': 'Manage financial services.',
  'Developer Settings': 'Configure external integration API keys.',
  'Super User Dashboard': 'System-wide admin controls.',
  'App Store': 'Browse and install vendor applications.',
  'Point of Sale': 'In-person touch register for retail.',
  'Lifestyle Planning': 'Monitor lifestyle members, families, events, and platform health.'
};

export const normalizeCardKeyValue = (name: string | null | undefined): string | null => {
  if (!name) {
    return null;
  }
  const trimmed = typeof name === 'string' ? name.trim() : name;
  return CARD_KEY_ALIASES[trimmed] || trimmed;
};

export const DEFAULT_CATEGORY_META: Record<string, CategoryMeta> = {
  'access control': { name: 'Access Control', icon: ShieldCheckIcon, iconKey: 'ShieldCheckIcon', textClass: 'text-indigo-600' },
  system: { name: 'System', icon: ChartBarIcon, iconKey: 'ChartBarIcon', textClass: 'text-indigo-600' },
  applications: { name: 'Applications', icon: CodeBracketIcon, iconKey: 'CodeBracketIcon', textClass: 'text-green-600' },
  other: { name: 'Other', icon: Squares2X2Icon, iconKey: 'Squares2X2Icon', textClass: 'text-gray-600' }
};

export const DASHBOARD_DEFAULT_CATEGORY_KEYS = ['access control', 'system', 'applications'] as const;
export const DEFAULT_CATEGORY_KEYS = Object.keys(DEFAULT_CATEGORY_META);

export const getDefaultCategoryName = (key: string): string => DEFAULT_CATEGORY_META[key]?.name || key;

export const ALWAYS_VISIBLE_CARD_KEYS = new Set(['User Management', 'Login Management']);

export const isAlwaysVisibleCard = (key: string): boolean => {
  const canonical = normalizeCardKeyValue(key);
  return Boolean(canonical && ALWAYS_VISIBLE_CARD_KEYS.has(canonical));
};

export const findCardCategory = (cardKey: string, cardOrganization: CardOrganization): string | null => {
  const canonical = normalizeCardKeyValue(cardKey);
  if (!canonical) return null;
  for (const [category, cards] of Object.entries(cardOrganization)) {
    if ((cards || []).some((name) => normalizeCardKeyValue(name) === canonical)) {
      return category;
    }
  }
  return null;
};

export const removeCardFromOrganization = (
  organization: CardOrganization,
  cardKey: string
): CardOrganization => {
  const canonical = normalizeCardKeyValue(cardKey);
  const updated: Record<string, string[]> = {};
  Object.entries(organization).forEach(([category, cards]) => {
    updated[category] = (cards || []).filter((name) => normalizeCardKeyValue(name) !== canonical);
  });
  return updated as CardOrganization;
};

export const ensureCardInOrganization = (
  organization: CardOrganization,
  cardKey: string,
  fallbackCategory = 'other',
  options: { insertIndex?: number | null } = {}
): CardOrganization => {
  const canonical = normalizeCardKeyValue(cardKey);
  if (!canonical) {
    return organization;
  }

  const exists = Object.values(organization).some((cards) =>
    (cards || []).some((name) => normalizeCardKeyValue(name) === canonical)
  );
  if (exists) {
    return organization;
  }

  const targetCategory = fallbackCategory || 'other';
  const { insertIndex = null } = options;
  const updated = { ...organization };
  const filteredList = (updated[targetCategory] || []).filter(
    (name) => normalizeCardKeyValue(name) !== canonical
  );

  if (insertIndex !== null && insertIndex >= 0 && insertIndex <= filteredList.length) {
    filteredList.splice(insertIndex, 0, canonical);
  } else {
    filteredList.push(canonical);
  }

  updated[targetCategory] = filteredList;
  return updated;
};

export const isCustomCategory = (
  categoryKey: string,
  customCategories: CustomCategoriesMap
): boolean => {
  const category = customCategories[categoryKey];
  return Boolean(category?.icon && category?.color);
};

export const resolveCategoryDisplayInfo = (
  categoryKey: string,
  customCategories: CustomCategoriesMap
): CategoryDisplayInfo => {
  if (!categoryKey) {
    return { name: 'Unknown', icon: Squares2X2Icon, color: 'text-gray-600' };
  }

  const category = customCategories[categoryKey];
  if (category?.icon && category?.color) {
    const IconComponent = iconOptions[category.icon] || Squares2X2Icon;
    return {
      name: category.name || categoryKey,
      icon: IconComponent,
      color: category.color
    };
  }
  if (category?.name) {
    const defaults = DEFAULT_CATEGORY_META[categoryKey] || { icon: Squares2X2Icon, textClass: 'text-gray-600' };
    return {
      name: category.name,
      icon: defaults.icon,
      color: defaults.textClass
    };
  }

  const defaults = DEFAULT_CATEGORY_META[categoryKey];
  if (defaults) {
    return {
      name: defaults.name,
      icon: defaults.icon,
      color: defaults.textClass
    };
  }

  return { name: categoryKey, icon: Squares2X2Icon, color: 'text-gray-600' };
};

export const buildCategoryForms = (
  cardOrganization: CardOrganization,
  customCategories: CustomCategoriesMap
): CategoryFormsMap => {
  const categoryKeys = Array.from(
    new Set([
      ...DEFAULT_CATEGORY_KEYS,
      ...Object.keys(cardOrganization),
      ...Object.keys(customCategories)
    ])
  ).filter(Boolean);

  const forms: CategoryFormsMap = {};
  categoryKeys.forEach((key) => {
    const info = resolveCategoryDisplayInfo(key, customCategories);
    const custom = customCategories[key] || {};
    forms[key] = {
      name: info.name || getDefaultCategoryName(key),
      icon: custom.icon || DEFAULT_CATEGORY_META[key]?.iconKey || 'Squares2X2Icon',
      color: custom.color || 'bg-gray-500',
      isCustom: isCustomCategory(key, customCategories),
      defaultName: getDefaultCategoryName(key)
    };
  });
  return forms;
};

export const shouldRenderDashboardCategory = (
  categoryKey: string,
  visibleCardCount: number,
  customCategories: CustomCategoriesMap,
  cardOrganization: CardOrganization
): boolean => {
  if (visibleCardCount > 0) {
    return true;
  }

  if (Object.prototype.hasOwnProperty.call(customCategories, categoryKey)) {
    return true;
  }

  return (
    Object.prototype.hasOwnProperty.call(cardOrganization, categoryKey) &&
    !DASHBOARD_DEFAULT_CATEGORY_KEYS.includes(categoryKey as (typeof DASHBOARD_DEFAULT_CATEGORY_KEYS)[number]) &&
    categoryKey !== 'other'
  );
};

export const normalizeCategoryKey = (key: string): string => {
  if (!key || typeof key !== 'string') return key;
  return key.toLowerCase().trim();
};

export function normalizeOrganizationKeys(organization: CardOrganization = {}): CardOrganization {
  const normalized: CardOrganization = {};
  const seen = new Set<string>();

  Object.keys(organization).forEach((category) => {
    const normalizedCategory = normalizeCategoryKey(category);
    const cards = Array.isArray(organization[category]) ? organization[category] : [];

    const existingCards = normalized[normalizedCategory] || [];
    const unique = [...existingCards];

    cards.forEach((cardName) => {
      const key = normalizeCardKeyValue(cardName);
      if (!key) {
        return;
      }
      if (!seen.has(key)) {
        unique.push(key);
        seen.add(key);
      }
    });

    normalized[normalizedCategory] = unique;
  });

  return normalized;
}

export const iconOptions: IconOptionsMap = {
  Squares2X2Icon,
  ShoppingBagIcon,
  ChartBarIcon,
  TagIcon,
  ShieldCheckIcon,
  CubeIcon,
  CreditCardIcon,
  TruckIcon,
  UserIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
  TicketIcon,
  BriefcaseIcon,
  BookOpenIcon,
  BellAlertIcon,
  ClockIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  WalletIcon,
  LightBulbIcon,
  CalculatorIcon,
  DevicePhoneMobileIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  CircleStackIcon,
  CloudIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
  CurrencyDollarIcon,
  HeartIcon,
  TrophyIcon,
};

export const VALID_CARD_COLORS = new Set([
  'bg-blue-500',
  'bg-blue-600',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-orange-600',
  'bg-red-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-indigo-600',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-amber-600',
  'bg-gray-500',
  'bg-slate-500',
  'bg-slate-600',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-emerald-600',
  'bg-lime-500',
  'bg-rose-500',
  'bg-sky-500',
  'bg-fuchsia-500',
  'bg-yellow-500',
  'bg-gray-600'
]);

export function normalizeCardSettings(settings: Record<string, CardSettingsEntry> = {}): CardSettingsMap {
  if (!settings || typeof settings !== 'object') {
    return {};
  }

  const normalized: CardSettingsMap = {};

  Object.entries(settings).forEach(([key, value]) => {
    const canonicalKey = normalizeCardKeyValue(key);
    if (!canonicalKey) {
      return;
    }

    const entry: CardSettingsEntry = value || {};
    const normalizedEntry: CardSettingsEntry = {};

    if (typeof entry.displayName === 'string' && entry.displayName.trim()) {
      if (canonicalKey === 'User Management' && entry.displayName.trim().toLowerCase() === 'users') {
        // Strip legacy 'Users' display name override
      } else {
        normalizedEntry.displayName = entry.displayName.trim();
      }
    }

    if (typeof entry.link === 'string') {
      const trimmedLink = entry.link.trim();
      if (trimmedLink) {
        normalizedEntry.link = trimmedLink;
      }
    }

    if (typeof entry.icon === 'string' && entry.icon in iconOptions) {
      normalizedEntry.icon = entry.icon;
    }

    if (typeof entry.color === 'string' && VALID_CARD_COLORS.has(entry.color)) {
      normalizedEntry.color = entry.color;
    }

    if (entry.hidden && !isAlwaysVisibleCard(canonicalKey)) {
      normalizedEntry.hidden = true;
    }

    if (entry.lastCategory && typeof entry.lastCategory === 'string') {
      normalizedEntry.lastCategory = entry.lastCategory;
    }

    if (Object.keys(normalizedEntry).length > 0) {
      normalized[canonicalKey] = normalizedEntry;
    }
  });

  return normalized;
}

export const getDefaultCardOrganization = (): CardOrganization => {
  return {
    'access control': ['User Management', 'ACL Management', 'Roles', 'Login Management'],
    applications: [
      'Landing CMS',
      'Website Builder',
      'Website Manager',
      'Domain Management',
      'App Store',
      'Point of Sale',
      'Lifestyle Planning'
    ],
    system: ['User Management', 'Systems Control', 'Server Manager', 'Analytics', 'System Analytics', 'Login Management', 'Compliance Portal', 'Plesk Management', 'Blockchain Management', 'Inactive Companies', 'Projects', 'Idea Flow', 'Cost Model Profiles', 'Bot Settings', 'Unfinished Orders', 'Knowledge Base', 'Learning Management', 'LMS Settings', 'Memberships', 'Investors', 'KYC Providers', 'Mail', 'SMS', 'SIP', 'Docker', 'Document Management', 'Cloud Storage', 'E-Signature', 'Template Manager', 'Legal Management', 'CRM Management', 'Workflow Designer', 'Backup Management', 'AI Management', 'Daily Quotes', 'Accounting Management', 'HR Management', 'Support Tickets', 'Video Conference', 'Janus Management', 'Janus Admin', 'Janus Transcription', 'Log Viewer', 'Chat Management', 'Navbar Editor', 'Sidebar Editor', 'Types Management', 'Developer Settings', 'Super User Dashboard'],
    other: ['Products', 'Total Companies', 'Inventory', 'Payments', 'Coupons', 'Suppliers', 'Financial Services', 'Investment Pools', 'Real Estate', 'Sports', 'Automotive']
  };
};

export const COLOR_OPTIONS = [
  { name: 'Gray', value: 'bg-gray-500' },
  { name: 'Slate', value: 'bg-slate-500' },
  { name: 'Red', value: 'bg-red-500' },
  { name: 'Orange', value: 'bg-orange-500' },
  { name: 'Amber', value: 'bg-amber-500' },
  { name: 'Yellow', value: 'bg-yellow-500' },
  { name: 'Lime', value: 'bg-lime-500' },
  { name: 'Green', value: 'bg-green-500' },
  { name: 'Emerald', value: 'bg-emerald-500' },
  { name: 'Teal', value: 'bg-teal-500' },
  { name: 'Cyan', value: 'bg-cyan-500' },
  { name: 'Sky', value: 'bg-sky-500' },
  { name: 'Blue', value: 'bg-blue-500' },
  { name: 'Indigo', value: 'bg-indigo-500' },
  { name: 'Violet', value: 'bg-violet-500' },
  { name: 'Purple', value: 'bg-purple-500' },
  { name: 'Fuchsia', value: 'bg-fuchsia-500' },
  { name: 'Pink', value: 'bg-pink-500' },
  { name: 'Rose', value: 'bg-rose-500' }
];

export const DEPRECATED_CARDS: Record<string, string> = {
  'wholesale listings': 'listings',
  'total products': 'products',
  'shipping carriers': 'carriers',
  'legal portal': 'legal management',
  'shipping': '',
  'carriers': '',
  'services': '',
  'deals': '',
};
